import React, { FunctionComponent } from "react";
import { colors, ConfigProvider } from "@ui-kit/ConfigProvider/ConfigProvider";
import { Button } from "antd";
import { ButtonHTMLType } from "antd/es/button";

type TabButtonVariant =
  | "default"
  | "inverse"
  | "inverseTranslucent"
  | "destructive"
  | "strong"
  | "primary"
  | "outline"
  | "ai";

interface TabButtonProps {
  /**
   * Callback when button is pressed.
   */
  onPress?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Label that appears on button.
   */
  label?: string;
  /**
   * Icon to appear before label.
   */
  iconPrefix?: React.ReactNode;
  /**
   * Icon to appear after label.
   */
  iconSuffix?: React.ReactNode;
  /**
   * If button is in selected state. Will change background to client primary color.
   */
  active?: boolean;
  /**
   * Size of the button.
   */
  size?: "default" | "large" | "small" | "small-default";
  /**
   * If button is disabled.
   */
  disabled?: boolean;
  /**
   * Adds additional horizontal/vertical padding.
   */
  wide?: boolean;
  /**
   * Adds additional horizontal padding.
   */
  medium?: boolean;
  /**
   * Let horizontal padding flex.
   */
  flex?: boolean;
  /**
   * Variant of the button.
   */
  variant?: TabButtonVariant;
  testID?: string;
  raised?: boolean;
  bigButton?: boolean;
  glow?: boolean;
  fullWidth?: boolean;
  /**
   * Set the original html type of button, @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#type
   * @default "button"
   */
  htmlType?: ButtonHTMLType;
  ref?: React.RefObject<HTMLButtonElement>;
}

type variantMap = Record<TabButtonVariant, string>;

const backgroundColorMap: variantMap = {
  default: colors.neutral200,
  inverse: colors.white,
  destructive: colors.red800,
  strong: colors.neutral200,
  primary: colors.primary60,
  outline: colors.white,
  inverseTranslucent: "rgba(255, 255, 255, 0.8)",
  ai: "linear-gradient(75deg, #EDFCFE 23.15%, #CDE1FF 92%)",
};

const backgroundHoverColorMap: variantMap = {
  default: colors.neutral300,
  inverse: colors.neutral300,
  destructive: colors.red900,
  strong: colors.neutral300,
  primary: colors.primary70,
  outline: colors.neutral300,
  inverseTranslucent: "rgba(240, 241, 242, 0.8)",
  ai: "linear-gradient(74.52deg, #BBF4FC 23.15%, #99C2FF 92%)",
};

const activeBackgroundColorMap: variantMap = {
  default: colors.primary60,
  inverse: colors.white,
  destructive: colors.red800,
  strong: colors.neutral200,
  primary: colors.primary60,
  outline: colors.primary60,
  inverseTranslucent: "rgba(255, 255, 255, 0.8)",
  ai: "linear-gradient(75deg, #EDFCFE 23.15%, #CDE1FF 92%)",
};

const textColorMap: variantMap = {
  default: colors.textPrimary,
  inverse: colors.textPrimary,
  destructive: colors.white,
  strong: colors.textPrimary,
  primary: colors.white,
  outline: colors.textPrimary,
  inverseTranslucent: colors.textPrimary,
  ai: colors.textPrimary,
};

const activeTextColorMap: variantMap = {
  default: colors.white,
  inverse: colors.primary60,
  destructive: colors.white,
  strong: colors.textPrimary,
  primary: colors.white,
  outline: colors.white,
  inverseTranslucent: colors.primary60,
  ai: colors.textPrimary,
};

const heightMap = {
  default: 32,
  large: 40,
  small: 20,
};

const fontSizeMap = {
  default: 14,
  large: 16,
  small: 12,
};

const TabButton: FunctionComponent<TabButtonProps> = ({
  onPress,
  label,
  iconPrefix,
  iconSuffix,
  active = false,
  size = "default",
  disabled = false,
  variant = "default",
  testID,
  wide = false,
  medium = false,
  raised = false,
  bigButton = false,
  glow = false,
  flex = false,
  fullWidth = false,
  htmlType = "button",
  ref,
}) => {
  const primaryColor = colors.primary60;
  let backgroundColor = active
    ? activeBackgroundColorMap[variant]
    : backgroundColorMap[variant];

  const height = heightMap[size];
  const fontSize = fontSizeMap[size];
  const textColor = active
    ? activeTextColorMap[variant]
    : textColorMap[variant];
  const hoverBackgroundColor = backgroundHoverColorMap[variant];

  return (
    <span
      data-testid={testID}
      style={flex ? { flex: 1, display: "flex" } : undefined}>
      <ConfigProvider
        themeConfigOverride={{
          components: {
            Button: {
              colorPrimary: backgroundColor,
              primaryShadow: "none",
              fontWeight: 500,
              primaryColor: textColor,
              solidTextColor: textColor,
              colorPrimaryHover: hoverBackgroundColor,
              colorPrimaryActive: hoverBackgroundColor,
              fontSize,
            },
          },
        }}>
        <Button
          style={{
            height,
            padding:
              wide && flex
                ? "20px 0"
                : wide
                  ? "20px 40px"
                  : medium
                    ? "0px 16px"
                    : "0px 8px",
            ...(active && variant === "inverse"
              ? { border: `1px solid ${primaryColor}` }
              : {}),
            ...(variant === "outline"
              ? { border: `1px solid ${colors.neutral300}` }
              : {}),
            ...(variant === "inverseTranslucent"
              ? { backdropFilter: "blur(6px)" }
              : {}),
            ...(raised
              ? { boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.15)" }
              : {}),
            ...(bigButton
              ? {
                  boxShadow:
                    "0px 2px 8px 0px rgba(0, 0, 0, 0.15), 0px 15px 30px 0px rgba(0, 0, 0, 0.15)",
                }
              : {}),
            gap: size === "small" ? "4px" : "6px",
            ...(flex ? { flex: 1 } : {}),
            ...(glow ? { boxShadow: "0px 0px 16px 0px #28A8FD" } : {}),
            ...(variant === "ai"
              ? {
                  border: "1px solid #BBF4FC",
                }
              : {}),
          }}
          onClick={onPress}
          type="primary"
          shape="round"
          disabled={disabled}
          block={fullWidth}
          htmlType={htmlType}
          ref={ref}>
          {iconPrefix}
          {label}
          {iconSuffix}
        </Button>
      </ConfigProvider>
    </span>
  );
};

export { TabButton, TabButtonVariant };
